import { countBy } from 'lodash-es'
import { useMemo } from 'react'
import {
  INBOX_CATEGORIES,
  type InboxCategoryId,
} from '#src/hooks/inbox/inboxCategories.ts'
import type { InboxListItem } from '#src/hooks/inbox/useInboxDedupedItems.ts'

/*
 * Recomputes category counts from the deduped (grouped) item list rather than
 * the raw notification list. This ensures the counts shown in the category bar
 * match the number of visible rows after deduplication.
 *
 * Items whose underlying Task is already completed or cancelled are excluded
 * from every count — these are stale notifications for done work and would
 * otherwise inflate the counters even though the items themselves are still
 * rendered in the list (with a "Completed" subtitle).
 */
export function useInboxDedupedCounts(
  dedupedItems: InboxListItem[],
  categoryMap: Map<string, InboxCategoryId>,
  statItemIds: Set<string>,
  overdueItemIds: Set<string>,
  completedTaskItemIds: Set<string>
): Record<InboxCategoryId, number> {
  return useMemo(() => {
    const countableItems = dedupedItems.filter(
      (li) => !completedTaskItemIds.has(li.item.id)
    )

    const categoryCounts = countBy(
      countableItems,
      (li) => categoryMap.get(li.item.id) ?? 'other-tasks'
    )

    const result = {} as Record<InboxCategoryId, number>
    for (const cat of INBOX_CATEGORIES) {
      if (cat.id === 'all') {
        result[cat.id] = countableItems.length
      } else if (cat.id === 'stat') {
        /* Count deduped items that are stat-priority */
        result[cat.id] = countableItems.filter((li) =>
          statItemIds.has(li.item.id)
        ).length
      } else if (cat.id === 'overdue') {
        /* Count deduped items that are overdue */
        result[cat.id] = countableItems.filter((li) =>
          overdueItemIds.has(li.item.id)
        ).length
      } else {
        result[cat.id] = categoryCounts[cat.id] ?? 0
      }
    }
    return result
  }, [
    dedupedItems,
    categoryMap,
    statItemIds,
    overdueItemIds,
    completedTaskItemIds,
  ])
}
